import { useState, useCallback, useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import type { JobData, ElementItem } from "./types";
import {
  RunIcon,
  MulRunIcon,
  ResultIcon,
  CopyIcon,
  ImportIcon,
  CreatDesignIcon,
  EditIcon,
  DeleteIcon,
  UpDownIcon,
} from "./Icons";
import "./TreeMenu.less";

import { message, Popover, Modal, Popconfirm, InputNumber, Checkbox, Select, Input } from "antd";
import {
  LoadingOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  WarningFilled,
  DeleteOutlined,
  DeleteFilled,
} from "@ant-design/icons";
import userInfo from "@/Components/user";
import { jobs, simulation, deleteJob, getJob, getSubJobs, delSubJobs, updateSubJob,getSubJob } from "@/services/common";
import isEqual from "lodash/isEqual";
import { typeMap3 } from "@/constants/dataMap";
import { buildNodeDataByInput } from "../func/buildNodeDataByInput";
import GroupCalParamsPreview from "@/Components/Diagram/ParamsPreview";
import dayjs from "dayjs";
// import TaskList from "../taskList";

const statusColor: any = {
  error: "#f10202",
  done: "#01894c",
  pedding: "#0066cc",
  uncovered: "#ffb833",
};

/** TreeMenu 组件的 props */
interface TreeMenuProps {
  /** 菜单数据源，由上层组件解析传递得到 */
  data: JobData[];
  /** 当前选中的节点信息，包含完整层级路径 */
  selectedNode: SelectedNode | null;
  /** 选中节点的回调函数，用于更新当前选中的节点信息 */
  setSelectedNode: (node: SelectedNode | null) => void;
  alreadyTaskIds: any;
  setAlreadyTaskIds: (newAlreadyTaskIds: any) => void;
  setTaskList: (newTaskList: any) => void;
  jsonData: any;
  onClickEvent: (
    newTaskId: number,
    newElementType: string,
    newElementName: string,
    newData: any,
  ) => void;
  newTreeData: any;
  clearSelect: boolean;
  state: any;
  resultDrawOpen?: any;
  setResultDrawOpen?: any;
  setGroupeCalOpen?: any;
  setImportVariable: (key, node) => void;
  expandFirstItemVariable: any;
  setCreatDesign: (key, node) => void;
  setRunElement: (key, node, maxWaitMinutes?: number) => void;
  setCreatTaskNameVariable: (newCreatTaskNameVariable) => void;
  setRunjob: (maxWaitMinutes?: number) => void;
  setRunComboTask?: (jobId: number, name: string, runs: any[], maxWaitMinutes: number, lastSubjobId?: number | null) => void;
  setCurResultInfo?: any;
  setUpTaskNameVariable: (newUpTaskNameVariable) => void;
  setDeleteJob?: (node) => void;
  expandedJobsVariable?: number;
  needSaveTaskVariable?: number;
  openSaveWarning?: (newTaskId) => void;
  setSelectGroup: (newSetSelectGroup) => void;
  setIsNowNeedOpenPramDrawer:(newIsNowNeedOpenPramDrawer)=>void;
  upParamDrawer:(node)=>void;
}

/** 单个操作按钮的配置 */
interface ActionConfig {
  /** 按钮唯一标识 */
  key: string;
  /** 按钮图标 */
  icon: ReactNode;
  /** 鼠标悬停时显示的提示文本 */
  title: string;
}

/** 一级菜单（job）右侧的功能按钮配置 */
const JOB_ACTIONS: ActionConfig[] = [
  { key: "runjob", icon: <RunIcon />, title: "运行任务" },
  // { key: "upTaskName", icon: <EditIcon />, title: "修改任务名称" },
  { key: "mulRun", icon: <MulRunIcon />, title: "单元组合运算" },
  { key: "resultjob", icon: <ResultIcon />, title: "查看任务结果" },
  // { key: "copyjob", icon: <CopyIcon />, title: "复制当前任务" },
  // { key: "importjob", icon: <ImportIcon />, title: "导入输入数据" },
  // { key: "creatDesign", icon: <CreatDesignIcon />, title: "创建设计值/运行数据" },
  // { key: "deleteJob", icon: <DeleteIcon />, title: "删除任务" },
  { key: "upDown", icon: <UpDownIcon />, title: "更多功能" },
];
/** 一级菜单（job）右侧的功能按钮配置 */
const MORE_ACTIONS: ActionConfig[] = [
  { key: "upTaskName", icon: <EditIcon />, title: "修改任务名称" },
  { key: "copyjob", icon: <CopyIcon />, title: "复制当前任务" },
  { key: "importjob", icon: <ImportIcon />, title: "导入输入数据" },
  { key: "creatDesign", icon: <CreatDesignIcon />, title: "创建设计值/运行数据" },
  { key: "deleteJob", icon: <DeleteIcon />, title: "删除任务" },
];

/** 单元操作节点（四级）右侧的操作按钮配置 */
const ELEMENT_ACTIONS: ActionConfig[] = [
  { key: "upParamDrawer", icon: <EditIcon />, title: "编辑参数" },
  { key: "runElement", icon: <RunIcon />, title: "运行单元操作" },
  { key: "resultElement", icon: <ResultIcon />, title: "查看单元操作结果" },
  { key: "importElement", icon: <ImportIcon />, title: "导入单元操作输入数据" },
];

/** 流股子节点（三级）右侧的操作按钮配置 */
const STREAM_ACTIONS: ActionConfig[] = [
  // { key: 'run', icon: <RunIcon />, title: '运行' },
  { key: "upParamDrawer", icon: <EditIcon />, title: "编辑参数" },
  { key: "importStream", icon: <ImportIcon />, title: "导入流股输入数据" },
];

/**
 * 树形菜单主组件
 *
 * 交互规则：
 * 1. 只能有一个一级菜单展开，切换时其他一级菜单自动收起
 * 2. 一级菜单收起后，右侧按钮功能区自动隐藏
 * 3. 末级菜单在 hover 或选中后，右侧显示操作按钮
 * 4. 点击任意节点会有蓝色高亮选中标识
 * 5. 每次点击都会在控制台输出包含完整层级路径的选中信息
 */
export default function TreeMenu({
  data,
  selectedNode,
  setSelectedNode,
  alreadyTaskIds,
  setAlreadyTaskIds,
  setTaskList,
  jsonData,
  onClickEvent,
  newTreeData,
  clearSelect,
  state,
  resultDrawOpen,
  setResultDrawOpen,
  setGroupeCalOpen,
  setImportVariable,
  expandFirstItemVariable,
  setCreatDesign,
  setRunElement,
  setCreatTaskNameVariable,
  setRunjob,
  setRunComboTask,
  setCurResultInfo,
  setUpTaskNameVariable,
  setDeleteJob,
  expandedJobsVariable,
  needSaveTaskVariable,
  openSaveWarning,
  setSelectGroup,
  setIsNowNeedOpenPramDrawer,
  upParamDrawer,
}: TreeMenuProps) {
  /** 当前展开的一级菜单 jobname 集合（互斥，最多一个） */
  // const [expandedJobs, setExpandedJobs] = useState<Set<string>>(() => {
  //   if (data.length > 0) return new Set([data[0].jobname]);
  //   return new Set();
  // });
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());

  /** 当前展开的二级分组 key 集合（单元操作 / 流股） */
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  /** 当前展开的三级分类 key 集合（塔、换热器等） */
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  //单元loading
  const [loadingElement, setLoadingElement] = useState<string>(null);
  const isLoadingRef = useRef(false);

  /** 标记是否是内部点击触发选中，用于区分外部操作和内部点击 */
  const isInternalRef = useRef(false);

  const [clickedSubJobKey, setClickedSubJobKey] = useState<string | null>(null); //被点击项(仅单个组合计算)

  //运行确认弹窗（含最大计算时间输入）
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [runModalMode, setRunModalMode] = useState<"job" | "element" | "combo">("job");
  const [runModalKey, setRunModalKey] = useState<string | null>(null);
  const [runModalNode, setRunModalNode] = useState<any>(undefined);
  const [maxWaitMinutes, setMaxWaitMinutes] = useState<number | null>(30); //最大计算时间(分)，默认30；允许删空再输入
  // 组合任务：是否基于过往组合计算结果进行计算（默认不选择，选择后提交时携带 last_subjob_id）
  const [useLastSubjob, setUseLastSubjob] = useState(false);
  const [lastSubjobId, setLastSubjobId] = useState<number | null>(null);
  // 组合任务运行弹窗：该组合选中元件的参数预览（取最近一次运行记录的 input.selected_blocks）
  const [comboSelectedBlocks, setComboSelectedBlocks] = useState<string[]>([]);

  // 打开组合任务运行弹窗时，通过最近一次运行记录 id 查询 getSubJob，取 input.selected_blocks 作为选中的元件
  useEffect(() => {
    if (runModalOpen && runModalMode === "combo" && runModalNode?.runs?.[0]?.id) {
      let cancelled = false;
      getSubJob(runModalNode.runs[0].id, userInfo.access_token)
        .then((res: any) => {
          if (cancelled) return;
          if (res.status === 200) {
            setComboSelectedBlocks(res.data?.input?.selected_blocks ?? []);
          }
        })
        .catch(() => {});
      return () => {
        cancelled = true;
      };
    } else {
      setComboSelectedBlocks([]);
    }
  }, [runModalOpen, runModalMode, runModalNode]);

  //组合重命名弹窗：整组改名（该组合名下全部运行记录一起改）
  const [renameCombo, setRenameCombo] = useState<{
    jobId: number;
    name: string;
    runs: any[];
  } | null>(null);
  const [renameComboName, setRenameComboName] = useState("");
  const [renameComboLoading, setRenameComboLoading] = useState(false);

  const [messageApi, contextHolder] = message.useMessage();
  const [loadingTask, setLoadingTask] = useState(null);
  const [needSaveTask, setNeedSaveTask] = useState(false);
  const [popupInfo, setPopupInfo] = useState<{
    visible: boolean;
    x: number;
    y: number;
    data?: [];
  }>({ visible: false, x: 0, y: 0, data: [] });
  const [popupInfo2, setPopupInfo2] = useState<{
    visible: boolean;
    x: number;
    y: number;
    data?: [];
  }>({ visible: false, x: 0, y: 0, data: [] });
  // 组合计算结果浮层（同名多次运行按子任务方式查看），与单元件"查看单元操作结果"浮层结构一致
  const [comboPopupInfo, setComboPopupInfo] = useState<{
    visible: boolean;
    x: number;
    y: number;
    name?: string;
    jobId?: number;
  }>({ visible: false, x: 0, y: 0 });
  const [comboSubJobs, setComboSubJobs] = useState<any[]>([]);
  const popupRef = useRef<HTMLDivElement>(null);
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});

  const [subJobs, setSubJobs] = useState<any>([]);

  console.log('runModalNode',runModalNode);


  // const [drawOpen,setDrawOpen]=useState(false)

  /**
   * 当外部传入 selectedNode 时，自动展开对应的层级
   * 根据 path 推导需要展开的 job、group、category
   */
  useEffect(() => {
    if (!selectedNode) return;
    // 内部点击触发选中时不强制展开，避免覆盖 toggleJob/toggleGroup 的收起操作
    if (isInternalRef.current) return;
    const path = selectedNode.path;
    if (!path || path.length === 0) return;

    // 展开一级菜单
    const jobname = path[0];
    setExpandedJobs(new Set([jobname]));

    if (path.length >= 2) {
      const group = path[1];
      const groupKey = `${jobname}::${group}`;
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        next.add(groupKey);
        return next;
      });

      if (path.length >= 3 && group === "单元操作") {
        const category = path[2];
        const catKey = `${jobname}::单元操作::${category}`;
        setExpandedCategories((prev) => {
          const next = new Set(prev);
          next.add(catKey);
          return next;
        });
      }
    }
  }, [selectedNode]);

  /**
   * 外部选中节点后，仅在左侧菜单容器内滚动，不触发页面滚动
   * 使用 requestAnimationFrame 等待展开动画渲染完成后再滚动
   */
  useEffect(() => {
    if (!selectedNode) return;
    // 内部点击触发选中时不滚动，仅外部操作选中时才滚动
    if (isInternalRef.current) {
      isInternalRef.current = false;
      return;
    }
    const raf = requestAnimationFrame(() => {
      const container = document.querySelector(".tree-container");
      const el = document.querySelector(".node-content.selected");
      if (container && el) {
        const containerRect = container.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        // 元素已完全在容器可视区域内，不滚动
        const isFullyVisible =
          elRect.top >= containerRect.top && elRect.bottom <= containerRect.bottom;
        if (isFullyVisible) return;
        const scrollTop =
          container.scrollTop +
          elRect.top -
          containerRect.top -
          containerRect.height / 2 +
          elRect.height / 2;
        container.scrollTo({ top: scrollTop, behavior: "smooth" });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [selectedNode]);

  useEffect(() => {
    if (newTreeData) {
      if (newTreeData.element && newTreeData.elementType && selectedNode) {
        //赋值给树的对应data
        let newTaskLists = structuredClone(data);
        let taskId = selectedNode.job_id;
        let key = newTreeData.element.key;
        let elementType = newTreeData.elementType;
        let newTaskList = newTaskLists.find((item) => item.job_id === taskId);
        if (newTaskList) {
          if (elementType === "node") {
            if (newTreeData.nodeType) {
              const elementArr: any = newTaskList.elements.find(
                (item) => item["engName"] === newTreeData.nodeType,
              ).children;
              elementArr[elementArr.findIndex((item) => item["data"].key === key)]["data"] =
                newTreeData.element;
              elementArr[elementArr.findIndex((item) => item["data"].key === key)]["name"] =
                newTreeData.element.nodeName;
              setTaskList(newTaskLists);
            }
          } else if (elementType === "link") {
            const index = newTaskList.streams.findIndex((item) => item["data"].key === key);
            newTaskList.streams[index]["data"] = newTreeData.element;
            newTaskList.streams[index]["name"] = newTreeData.element.linkName;
            setTaskList(newTaskLists);
          }

          //标记该任务需要保存
          setNeedSaveTask(true);
        }
      }
    }
    // }, [newTreeData?.element, newTreeData?.elementType, newTreeData?.nodeType]);
  }, [newTreeData]);

  useEffect(() => {
    clearSelected();
  }, [clearSelect]);

  useEffect(() => {
    if (expandFirstItemVariable) {
      let res = expandFirstItem(
        expandFirstItemVariable.selectTaskName,
        expandFirstItemVariable.selectTaskId,
      );
    }
  }, [expandFirstItemVariable]);

  useEffect(() => {
    if (resultDrawOpen === false) {
      setClickedSubJobKey(null);
    }
  }, [resultDrawOpen]);

  useEffect(() => {
    if (!selectedNode && expandedJobs.size > 0 && alreadyTaskIds.length === 0) {
      //如果没有选中但是仍然有展开，则选中该选项并且调用expandFirstItem展开函数(alreadyTaskIds.length===0是为了确保queryTask结束了)
      for (let i = 0; i < expandedJobs.size; i++) {
        let TaskName = Array.from(expandedJobs)[i];
        const task = data.find((item) => item.jobname === TaskName);
        if (task) {
          const TaskId = data.find((item) => item.jobname === TaskName).job_id;
          let res = expandFirstItem(TaskName, TaskId);
          handleSelect({
            type: "job",
            job_id: TaskId,
            job_name: TaskName,
            item_name: TaskName,
            path: [TaskName],
          });
        }
      }
    }
    // setClickedSubJobKey(null);
  }, [selectedNode, alreadyTaskIds]);

  useEffect(() => {
    if (data && alreadyTaskIds.length === 0 && selectedNode) {
      expandFirstItem(selectedNode.job_name, selectedNode.job_id);
    }
  }, [alreadyTaskIds]);

  useEffect(() => {
    if (expandedJobsVariable > 0) {
      //取消展开
      setExpandedJobs((prev) => {
        const next = new Set<string>();
        return next;
      });
    }
  }, [expandedJobsVariable]);

  useEffect(() => {
    if (needSaveTaskVariable > 0) {
      setNeedSaveTask(false);
    }
  }, [needSaveTaskVariable]);

  useEffect(() => {
    if (selectedNode) {
      if (selectedNode.path.length >= 3 && selectedNode.path[1] === "分组") {
        //选中了某个分组
        let groupName = selectedNode.path[2];
        if (groupName === "无分组") {
          setSelectGroup(null);
        } else {
          let task = data.find((item) => item.job_id === selectedNode.job_id);
          if (task) {
            let taskGroups = task.groups;
            let taskGroup = taskGroups.find((item) => item.name === groupName);
            if (taskGroup) {
              setSelectGroup(taskGroup.data);
            }
          }
        }
      }
    }
  }, [selectedNode]);

  /**
   * 切换一级菜单展开状态
   * 规则：互斥展开，同一时间只能有一个一级菜单打开
   */
  const toggleJob = useCallback(
    async (jobname: string, jobId: number) => {
      //查询任务数据
      setLoadingTask(jobname);
      let res = await expandFirstItem(jobname, jobId);

      setExpandedJobs((prev) => {
        const next = new Set<string>();
        // 如果当前未展开，则展开；如果已展开，则收起（集合为空）
        if (!prev.has(jobname)) {
          next.add(jobname);
        }
        return next;
      });
    },
    [data, alreadyTaskIds],
  );

  /** 切换二级分组（单元操作 / 流股）展开状态 */
  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  /** 切换三级分类（塔、换热器等）展开状态 */
  const toggleCategory = useCallback((key: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  /**
   * 节点操作按钮组
   * 渲染在节点右侧，hover 或选中时显示
   *
   * 点击按钮时，如果当前节点未被选中，会先触发 onBeforeAction 将其选中，
   * 然后再执行按钮自身的操作逻辑。
   */
  function ActionButtons({
    actions,
    label,
    onBeforeAction,
    node,
  }: {
    actions: ActionConfig[];
    label: string;
    /** 点击按钮前执行，用于先选中当前菜单项 */
    onBeforeAction?: () => void;
    /** 当前节点完整数据，点击按钮时直接传回调用方，避免读取异步 state */
    node?: SelectedNode;
  }) {
    return (
      <div className="node-actions">
        {actions.map((a) => {
          return (
            <button
              key={a.key}
              className="action-btn"
              title={a.title}
              onClick={(e) => {
                e.stopPropagation();
                // 先选中当前菜单项
                onBeforeAction?.();
                // console.log(a.key, label);
                // 执行按钮自身的操作逻辑
                buttonHandler(a.key, node, e);
              }}
            >
              {a.icon}
            </button>
          );
        })}
      </div>
    );
  }

  /**
   * 单个节点的内容渲染
   * 包括箭头、文字标签和可选的操作按钮
   */
  function NodeContent({
    label,
    hasChildren,
    isExpanded,
    isSelected,
    actions,
    onClick,
    icon,
    badge,
    style,
  }: {
    label: string;
    hasChildren: boolean;
    isExpanded: boolean;
    isSelected: boolean;
    actions?: ReactNode;
    onClick: (e: any) => void;
    icon?: ReactNode;
    badge?: boolean;
    style?: React.CSSProperties;
  }) {
    // 将样式拆分为根节点样式和 label 样式
    const { color, fontWeight, ...rootStyle } = style || {};
    const labelStyle = { color, fontWeight };

    return (
      <div
        className={`node-content${isSelected ? " selected" : ""}`}
        style={rootStyle}
        onClick={(e) => onClick(e)}
      >
        {/* 有子节点时显示展开箭头，否则留空占位 */}
        {hasChildren ? (
          <span className={`arrow${isExpanded ? " expanded" : ""}`}>▶</span>
        ) : (
          <span className="arrow-placeholder" />
        )}
        {badge && (
          <span className="calc-badge">
            <CheckCircleFilled style={{ fontSize: "16px", color: `${statusColor.done}` }} />
          </span>
        )}
        <span className="node-label" style={labelStyle}>
          {label}
        </span>
        {icon && <span className="node-icon">{icon}</span>}
        {actions}
      </div>
    );
  }

  // 弹出层位置自适应，防止超出屏幕
  useLayoutEffect(() => {
    if (popupInfo.visible && popupRef.current) {
      const rect = popupRef.current.getBoundingClientRect();
      const offset = 10;
      let left = popupInfo.x + offset;
      let top = popupInfo.y + offset;

      if (left + rect.width > window.innerWidth) {
        left = popupInfo.x - rect.width - offset;
      }
      if (top + rect.height > window.innerHeight) {
        top = popupInfo.y - rect.height - offset;
      }
      left = Math.max(offset, left);
      top = Math.max(offset, top);

      setPopupStyle({ left, top });
    }
  }, [popupInfo.visible, popupInfo.x, popupInfo.y]);

  useLayoutEffect(() => {
    if (popupInfo2.visible && popupRef.current) {
      const rect = popupRef.current.getBoundingClientRect();
      const offset = 10;
      let left = popupInfo2.x + offset;
      let top = popupInfo2.y + offset;

      if (left + rect.width > window.innerWidth) {
        left = popupInfo2.x - rect.width - offset;
      }
      if (top + rect.height > window.innerHeight) {
        top = popupInfo2.y - rect.height - offset;
      }
      left = Math.max(offset, left);
      top = Math.max(offset, top);

      setPopupStyle({ left, top });
    }
  }, [popupInfo2.visible, popupInfo2.x, popupInfo2.y]);

  useLayoutEffect(() => {
    if (comboPopupInfo.visible && popupRef.current) {
      const rect = popupRef.current.getBoundingClientRect();
      const offset = 10;
      let left = comboPopupInfo.x + offset;
      let top = comboPopupInfo.y + offset;

      if (left + rect.width > window.innerWidth) {
        left = comboPopupInfo.x - rect.width - offset;
      }
      if (top + rect.height > window.innerHeight) {
        top = comboPopupInfo.y - rect.height - offset;
      }
      left = Math.max(offset, left);
      top = Math.max(offset, top);

      setPopupStyle({ left, top });
    }
  }, [comboPopupInfo.visible, comboPopupInfo.x, comboPopupInfo.y]);
  /**
   * 右侧按钮点击事件处理
   * 按不同节点不同按钮进行处理
   */
  const buttonHandler = async (key: string, node?: SelectedNode, e?: any) => {
    // console.log(key);
    // console.log(node);
    if (key === "resultjob") {
      setCurResultInfo({
        id: node.job_id,
        type: "job",
      });
      setResultDrawOpen(true);
    } else if (key === "mulRun") {
      setGroupeCalOpen(true);
    } else if (key === "importjob" || key === "importElement" || key === "importStream") {
      if (key === "importElement" && node) {
        //单元件导入：与组合计算导入一致，附带该元件的历史运行记录，
        //弹窗内可新增"运行记录"来源，导入当前元件其他次任务的输入数据
        try {
          const res: any = await getSubJobs(
            { job_id: node.job_id, name: node.item_name },
            userInfo.access_token,
          );
          const runs = (res?.data || []).filter((i: any) => i.name === node.item_name);
          setImportVariable(key, { ...node, runs });
        } catch (err) {
          console.error("获取单元操作运行记录失败", err);
          setImportVariable(key, { ...node, runs: [] });
        }
      } else {
        setImportVariable(key, node);
      }
    } else if (key === "creatDesign") {
      setCreatDesign(key, node);
    } else if (key === "runElement") {
      setRunModalMode("element");
      setRunModalKey(key);
      setRunModalNode(node);
      setMaxWaitMinutes(30);
      setRunModalOpen(true);
    } else if (key === "resultElement") {
      if (isLoadingRef.current) return;
      setComboPopupInfo({ visible: false, x: 0, y: 0 });
      isLoadingRef.current = true;
      setLoadingElement(node.item_name);
      const token = userInfo.access_token;
      // console.log("selectedNode", selectedNode);
      setSubJobs([]);
      try {
        await getSubJobs(
          {
            //临时调用
            // job_id: 1616,
            // name: "T-106",
            job_id: node.job_id,
            name: node.item_name,
          },
          token,
        ).then((res: any) => {
          // console.log("werwerwerwr", res.data);
          if (res.status === 200) {
            setSubJobs(res.data.filter((i: any) => i.name === node.item_name));
            setPopupInfo({ visible: true, x: e.clientX, y: e.clientY });
            isLoadingRef.current = false;
            setLoadingElement(null);
          }
        });
      } catch (error) {
        console.error("加载失败:", error);
        isLoadingRef.current = false;
        setLoadingElement(null);
      }
    } else if (key === "copyjob") {
      setCreatTaskNameVariable({ open: true, isCopy: true });
    } else if (key === "runjob") {
      setRunModalMode("job");
      setRunModalKey(null);
      setRunModalNode(undefined);
      setMaxWaitMinutes(30);
      setRunModalOpen(true);
    } else if (key === "upTaskName") {
      setUpTaskNameVariable(true);
    } else if (key === "deleteJob") {
      Modal.confirm({
        // title: "确认删除",
        content: "是否确认删除？",
        onOk: () => {
          setDeleteJob(node);
        },
        okText: "确认",
        cancelText: "取消",
      });
    } else if (key === "upDown") {
      setPopupInfo2({ visible: true, x: e.clientX, y: e.clientY });
    }
    else if(key==="upParamDrawer")
    {
      //等同于点击树节点
      handleSelect(node);
      if (node.data) {
        onClickEvent(node.job_id, node.type, node.item_name, node.data);
      } else {
        message.error(`${node.item_name} 数据有误`);
      }

      setIsNowNeedOpenPramDrawer(true);
    }
  };

  /**
   * 运行确认弹窗的确认逻辑：校验最大计算时间>0，通过后执行运行并关闭弹窗
   */
  const confirmRunWithMaxWait = () => {
    const val = maxWaitMinutes;
    if (val === null || val === undefined || isNaN(Number(val)) || Number(val) <= 0) {
      messageApi.open({
        type: "warning",
        content: "请输入大于 0 的最大计算时间（分）",
      });
      return;
    }
    if (runModalMode === "job") {
      setRunjob(Number(val));
    } else if (runModalMode === "combo") {
      //重新运行组合任务：提交同名 type=multi 子任务，新增一次运行记录
      if (runModalNode && setRunComboTask) {
        setRunComboTask(
          runModalNode.jobId,
          runModalNode.name,
          runModalNode.runs,
          Number(val),
          //未勾选"基于过往组合计算结果"时不传 last_subjob_id
          useLastSubjob ? lastSubjobId : null,
        );
      }
    } else {
      setRunElement(runModalKey, runModalNode, Number(val));
    }
    setRunModalOpen(false);
  };

  /**
   * 打开"组合计算结果"浮层（子任务方式）：列出该组合名称下的多次运行记录
   */
  const openComboResultPopup = (e: any, jobId: number, name: string, runs: any[]) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    //与其他浮层互斥
    setPopupInfo({ visible: false, x: 0, y: 0 });
    setPopupInfo2({ visible: false, x: 0, y: 0 });
    setComboSubJobs(runs);
    setComboPopupInfo({
      visible: true,
      x: e?.clientX ?? 0,
      y: e?.clientY ?? 0,
      name,
      jobId,
    });
  };

  /**
   * 从任务树的 job.subJobs 中移除指定 id 的组合结果（浮层删除/整组删除后同步树）
   */
  const removeSubJobsFromTaskList = (jobId: number, ids: number[]) => {
    const newData = structuredClone(data);
    const targetJob = newData.find((item) => item.job_id === jobId);
    if (targetJob) {
      targetJob.subJobs = targetJob.subJobs.filter((item) => !ids.includes(item.id));
      setTaskList(newData);
    }
  };

  /**
   * 打开组合重命名弹窗（整组：该组合名下全部运行记录一起改名）
   */
  const openRenameCombo = (jobId: number, name: string, runs: any[]) => {
    setRenameCombo({ jobId, name, runs });
    setRenameComboName(name);
  };

  /**
   * 整组重命名：把该组合名下所有子任务改为新名称，并同步任务树与浮层数据
   */
  const confirmRenameCombo = async () => {
    if (!renameCombo) return;
    const { jobId, name, runs } = renameCombo;
    const newName = renameComboName.trim();
    if (newName === "") {
      message.warning("请输入组合名称");
      return;
    }
    if (newName === name) {
      setRenameCombo(null);
      return;
    }
    //重名校验：与同任务下其它组合名的校验保持一致（画布上首次创建组合时同样不允许重名）
    const targetJob = data.find((item) => item.job_id === jobId);
    const existNames = (targetJob?.subJobs || []).map((item: any) => item.name);
    if (existNames.includes(newName)) {
      message.warning("组合计算结果名称重复");
      return;
    }

    setRenameComboLoading(true);
    const token = userInfo.access_token;

    //整组改名：以最新数据取该组合名下的全部运行记录。
    //树上缓存的 runs 可能不含刚提交、正在计算的那一次（提交后要等计算结束才刷新），
    //所以这里不能只按本地 runs 判断，否则正在计算的那条会被漏改、从原组合里分出去。
    let groupRuns: any[] = [];
    try {
      const res0 = await getSubJobs({ job_id: jobId }, token);
      if (res0.status !== 200) {
        setRenameComboLoading(false);
        message.error("查询组合运行状态失败，请稍后重试");
        return;
      }
      groupRuns = (res0.data || []).filter(
        (item: any) => item.type === "multi" && item.name === name,
      );
    } catch (e) {
      console.error("查询组合运行状态失败:", e);
      setRenameComboLoading(false);
      message.error("查询组合运行状态失败，请稍后重试");
      return;
    }

    //只要该组合名下还有正在计算的任务（未完成且未出错），整组都不允许改名
    if (groupRuns.some((item: any) => item.status !== "done" && item.status !== "error")) {
      setRenameComboLoading(false);
      message.warning("该组合中有正在计算的任务，请完成计算后再试");
      return;
    }

    //本地可能存在未刷新到的记录，取并集一起改名，保证整组名称一致
    const ids = Array.from(
      new Set([...runs.map((r: any) => r.id), ...groupRuns.map((item: any) => item.id)]),
    );

    const resList = await Promise.all(
      ids.map((id: number) => updateSubJob(id, { name: newName }, token)),
    );
    setRenameComboLoading(false);

    if (resList.every((res: any) => res.status === 200)) {
      //任务树：该组合名下所有运行记录的 name 同步为新名称
      const newData = structuredClone(data);
      const jobItem = newData.find((item) => item.job_id === jobId);
      if (jobItem) {
        jobItem.subJobs = jobItem.subJobs.map((item: any) =>
          ids.includes(item.id) ? { ...item, name: newName } : item,
        );
        setTaskList(newData);
      }
      //浮层若正展示该组合，同步名称与列表
      setComboSubJobs((prev) =>
        prev.map((item: any) => (ids.includes(item.id) ? { ...item, name: newName } : item)),
      );
      setComboPopupInfo((prev) => (prev.visible ? { ...prev, name: newName } : prev));
      //改名后原高亮 key 失效，跟随新名称
      setClickedSubJobKey((prev) => (prev === `${jobId}::${name}` ? `${jobId}::${newName}` : prev));
      setRenameCombo(null);
      message.success("修改成功");
    } else if (resList.some((res: any) => res.status === 200)) {
      //部分成功会让整组名称不一致（未改的会单独成为一个组合），明确提示
      message.error("部分记录修改失败，请重试");
    } else {
      message.error("修改失败，请重试");
    }
  };

  /**
   * 处理选中单元操作的事件
   */
  const elementSelectedHandler = (node: SelectedNode) => {
    if (node.data) {
      onClickEvent(node.job_id, node.type, node.item_name, node.data);
    } else {
      message.error(`${node.item_name} 数据有误`);
    }
  };

  /**
   * 处理选中流股操作的事件
   */
  const streamSelectedHandler = (node: SelectedNode) => {
    if (node.data) {
      onClickEvent(node.job_id, node.type, node.item_name, node.data);
    } else {
      message.error(`${node.item_name} 数据有误`);
    }
  };

  /**
   * 单元操作分支组件
   * 层级结构：job > 单元操作 > elementname > children
   */
  function UnitOperationNode({
    job,
    isGroupExpanded,
    isCategoryExpanded,
    selectedNode,
    toggleGroup,
    toggleCategory,
    handleSelect,
  }: {
    job: JobData;
    isGroupExpanded: (key: string) => boolean;
    isCategoryExpanded: (key: string) => boolean;
    selectedNode: SelectedNode | null;
    toggleGroup: (key: string) => void;
    toggleCategory: (key: string) => void;
    handleSelect: (node: SelectedNode) => void;
  }) {
    // 用 jobname + 分组名生成唯一 key，用于展开状态管理
    const groupKey = `${job.jobname}::单元操作`;
    const expanded = isGroupExpanded(groupKey);
    const selected =
      selectedNode?.type === "group" &&
      selectedNode.job_name === job.jobname &&
      selectedNode.group === "单元操作";

    return (
      <li className="tree-node level-2">
        {/* 二级菜单：单元操作 */}
        <NodeContent
          label="单元操作"
          hasChildren={true}
          isExpanded={expanded}
          isSelected={selected}
          onClick={() => {
            toggleGroup(groupKey);
            handleSelect({
              type: "group",
              job_id: job.job_id,
              job_name: job.jobname,
              group: "单元操作",
              path: [job.jobname, "单元操作"],
            });
          }}
        />
        <ul className={`tree-list${expanded ? "" : " collapsed"}`}>
          {job.elements.map((el: ElementItem) => {
            // 用 jobname + 分组名 + 分类名生成唯一 key
            const catKey = `${job.jobname}::单元操作::${el.elementname}`;
            const catExpanded = isCategoryExpanded(catKey);
            const catSelected =
              selectedNode?.type === "category" &&
              selectedNode.job_name === job.jobname &&
              selectedNode.category === el.elementname;

            return (
              <li key={catKey} className="tree-node level-3">
                {/* 三级菜单：设备分类（塔、换热器等） */}
                <NodeContent
                  label={el.elementname}
                  hasChildren={true}
                  isExpanded={catExpanded}
                  isSelected={catSelected}
                  onClick={() => {
                    toggleCategory(catKey);
                    handleSelect({
                      type: "category",
                      job_id: job.job_id,
                      job_name: job.jobname,
                      category: el.elementname,
                      path: [job.jobname, "单元操作", el.elementname],
                    });
                  }}
                />
                <ul className={`tree-list${catExpanded ? "" : " collapsed"}`}>
                  {el.children.map((child: any) => {
                    const childSelected =
                      selectedNode?.type === "element" &&
                      selectedNode.job_name === job.jobname &&
                      selectedNode.category === el.elementname &&
                      selectedNode.item_name === child.name;
                    return (
                      <li key={`${catKey}::${child.name}`} className="tree-node level-4 leaf-node">
                        {/* 四级叶子节点：具体设备（塔1、塔2等） */}
                        <NodeContent
                          label={child.name}
                          hasChildren={false}
                          isExpanded={false}
                          isSelected={childSelected}
                          icon={loadingElement === child.name ? <LoadingOutlined /> : undefined}
                          badge={child.data?.isRun ?? false}
                          actions={
                            <ActionButtons
                              actions={ELEMENT_ACTIONS}
                              label={child.name}
                              onBeforeAction={() =>
                                handleSelect({
                                  type: "element",
                                  job_id: job.job_id,
                                  job_name: job.jobname,
                                  category: el.elementname,
                                  item_name: child.name,
                                  path: [job.jobname, "单元操作", el.elementname, child.name],
                                  data: child.data,
                                })
                              }
                              node={{
                                type: "element",
                                job_id: job.job_id,
                                job_name: job.jobname,
                                category: el.elementname,
                                item_name: child.name,
                                path: [job.jobname, "单元操作", el.elementname, child.name],
                                data: child.data,
                              }}
                            />
                          }
                          onClick={() => {
                            handleSelect({
                              type: "element",
                              job_id: job.job_id,
                              job_name: job.jobname,
                              category: el.elementname,
                              item_name: child.name,
                              path: [job.jobname, "单元操作", el.elementname, child.name],
                              data: child.data,
                            });
                            elementSelectedHandler({
                              type: "element",
                              job_id: job.job_id,
                              job_name: job.jobname,
                              category: el.elementname,
                              item_name: child.name,
                              path: [job.jobname, "单元操作", el.elementname, child.name],
                              data: child.data,
                            });
                          }}
                        />
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      </li>
    );
  }

  /**
   * 流股分支组件
   * 层级结构：job > 流股 > stream
   * 流股下没有四级分类，stream 直接是叶子节点
   */
  function StreamNode({
    job,
    isGroupExpanded,
    selectedNode,
    toggleGroup,
    handleSelect,
  }: {
    job: JobData;
    isGroupExpanded: (key: string) => boolean;
    selectedNode: SelectedNode | null;
    toggleGroup: (key: string) => void;
    handleSelect: (node: SelectedNode) => void;
  }) {
    const groupKey = `${job.jobname}::流股`;
    const expanded = isGroupExpanded(groupKey);
    const selected =
      selectedNode?.type === "group" &&
      selectedNode.job_name === job.jobname &&
      selectedNode.group === "流股";

    return (
      <li className="tree-node level-2">
        {/* 二级菜单：流股 */}
        <NodeContent
          label="流股"
          hasChildren={true}
          isExpanded={expanded}
          isSelected={selected}
          onClick={() => {
            toggleGroup(groupKey);
            handleSelect({
              type: "group",
              job_id: job.job_id,
              job_name: job.jobname,
              group: "流股",
              path: [job.jobname, "流股"],
            });
          }}
        />
        <ul className={`tree-list${expanded ? "" : " collapsed"}`}>
          {job.streams.map((stream: any) => {
            const streamSelected =
              selectedNode?.type === "stream" &&
              selectedNode.job_name === job.jobname &&
              selectedNode.item_name === stream.name;
            return (
              <li key={`${groupKey}::${stream.name}`} className="tree-node level-3 leaf-node">
                {/* 三级叶子节点：单个流股 */}
                <NodeContent
                  label={stream.name}
                  hasChildren={false}
                  isExpanded={false}
                  isSelected={streamSelected}
                  actions={
                    <ActionButtons
                      actions={STREAM_ACTIONS}
                      label={stream.name}
                      onBeforeAction={() =>
                        handleSelect({
                          type: "stream",
                          job_id: job.job_id,
                          job_name: job.jobname,
                          item_name: stream.name,
                          path: [job.jobname, "流股", stream.name],
                          data: stream.data,
                        })
                      }
                      node={{
                        type: "stream",
                        job_id: job.job_id,
                        job_name: job.jobname,
                        item_name: stream.name,
                        path: [job.jobname, "流股", stream.name],
                        data: stream.data,
                      }}
                    />
                  }
                  onClick={() => {
                    handleSelect({
                      type: "stream",
                      job_id: job.job_id,
                      job_name: job.jobname,
                      item_name: stream.name,
                      path: [job.jobname, "流股", stream.name],
                      data: stream.data,
                    });
                    streamSelectedHandler({
                      type: "stream",
                      job_id: job.job_id,
                      job_name: job.jobname,
                      item_name: stream.name,
                      path: [job.jobname, "流股", stream.name],
                      data: stream.data,
                    });
                  }}
                />
              </li>
            );
          })}
        </ul>
      </li>
    );
  }

  /**
   * 组合计算结果分支组件
   * 层级结构：job > 组合计算结果 > subJobs
   * 流股下没有四级分类，stream 直接是叶子节点
   */
  function SubJobsNode({
    job,
    isGroupExpanded,
    selectedNode,
    toggleGroup,
    handleSelect,
  }: {
    job: JobData;
    isGroupExpanded: (key: string) => boolean;
    selectedNode: SelectedNode | null;
    toggleGroup: (key: string) => void;
    handleSelect: (node: SelectedNode) => void;
  }) {
    const groupKey = `${job.jobname}::组合计算结果`;
    const expanded = isGroupExpanded(groupKey);
    const selected =
      selectedNode?.type === "group" &&
      selectedNode.job_name === job.jobname &&
      selectedNode.group === "组合计算结果";
    return (
      <li className="tree-node level-2">
        {/* 二级菜单：组合计算结果 */}
        <NodeContent
          label="组合计算结果"
          hasChildren={true}
          isExpanded={expanded}
          isSelected={selected}
          icon={
            <span
              className="subjob-combine-icon"
              title="单元组合计算"
              style={{ display: "inline-flex", cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                setGroupeCalOpen(true);
              }}
            >
              <MulRunIcon />
            </span>
          }
          onClick={() => {
            toggleGroup(groupKey);
            handleSelect({
              type: "group",
              job_id: job.job_id,
              job_name: job.jobname,
              group: "组合计算结果",
              path: [job.jobname, "组合计算结果"],
            });
          }}
        />
        <ul className={`tree-list${expanded ? "" : " collapsed"}`}>
          {job.subJobs
            .reduce((acc: any[], subJob: any) => {
              //按名称聚合：同名多次运行合并为一个树节点，浮层内按时间列出各次子任务（同单元件计算任务的形式）
              const exist = acc.find((g) => g.name === subJob.name);
              if (exist) {
                exist.runs.push(subJob);
              } else {
                acc.push({ name: subJob.name, runs: [subJob] });
              }
              return acc;
            }, [])
            .map(({ name, runs }: { name: string; runs: any[] }) => {
              const subJobKey = `${job.job_id}::${name}`;
              const isHighlighted = clickedSubJobKey === subJobKey;
              return (
                <li key={`${groupKey}::${name}`} className="tree-node level-3 leaf-node">
                  {/* 三级叶子节点：单个组合计算结果（同名多次运行聚合，点击/查看组合任务弹出子任务浮层） */}
                  <NodeContent
                    label={name}
                    hasChildren={false}
                    isExpanded={false}
                    //选中该组合项后操作按钮常显（与单元件/任务一致，hover 亦显示）
                    isSelected={isHighlighted}
                    style={
                      isHighlighted
                        ? { backgroundColor: "#e6f2ff", color: "#0066cc", fontWeight: 600 }
                        : undefined
                    }
                    onClick={(e) => {
                      setClickedSubJobKey(subJobKey);
                      if (runs.length === 1) {
                        //同名只有一次运行：保持原行为，直接打开该次组合结果
                        setCurResultInfo({ id: runs[0].id, type: "multiJobs" });
                        setResultDrawOpen(true);
                      } else {
                        //同名多次运行：弹出子任务列表供选择
                        openComboResultPopup(e, job.job_id, name, runs);
                      }
                    }}
                    actions={
                      <div className="node-actions">
                        <button
                          className="action-btn"
                          title="运行组合任务"
                          onClick={(e) => {
                            e.stopPropagation();
                            //点击操作按钮即视为选中该项，操作按钮随后常显
                            setClickedSubJobKey(subJobKey);
                            setRunModalMode("combo");
                            setRunModalKey(null);
                            setRunModalNode({ jobId: job.job_id, name, runs });
                            setMaxWaitMinutes(30);
                            //每次打开默认不基于过往结果
                            setUseLastSubjob(false);
                            setLastSubjobId(null);
                            setRunModalOpen(true);
                          }}
                        >
                          <RunIcon />
                        </button>
                        <button
                          className="action-btn"
                          title="查看组合任务"
                          onClick={(e) => {
                            e.stopPropagation();
                            setClickedSubJobKey(subJobKey);
                            openComboResultPopup(e, job.job_id, name, runs);
                          }}
                        >
                          <ResultIcon />
                        </button>
                        <button
                          className="action-btn"
                          title="导入组合任务数据"
                          onClick={(e) => {
                            e.stopPropagation();
                            //点击操作按钮即视为选中该项
                            setClickedSubJobKey(subJobKey);
                            //与单元件/全任务导入一致：打开"导入数据"弹窗，
                            //在"计算任务/设计值/实际值"同级新增"组合计算"来源，可选当前组合的其他运行结果
                            setImportVariable("importCombo", {
                              type: "combo",
                              job_id: job.job_id,
                              job_name: job.jobname,
                              item_name: name,
                              runs,
                            });
                          }}
                        >
                          <ImportIcon />
                        </button>
                        <button
                          className="action-btn"
                          title="重命名组合"
                          onClick={(e) => {
                            e.stopPropagation();
                            //点击操作按钮即视为选中该项
                            setClickedSubJobKey(subJobKey);
                            openRenameCombo(job.job_id, name, runs);
                          }}
                        >
                          <EditIcon />
                        </button>
                        <Popconfirm
                          title={
                            runs.length > 1
                              ? `是否确认删除该组合的 ${runs.length} 次计算结果？`
                              : "是否确认删除当前计算结果？"
                          }
                          zIndex={9000000}
                          onConfirm={async (e) => {
                            e.stopPropagation();
                            let token = userInfo.access_token;
                            const ids = runs.map((r: any) => r.id);
                            const resList = await Promise.all(
                              ids.map((id: number) => delSubJobs(id, token)),
                            );
                            if (resList.every((res: any) => res.status === 200)) {
                              removeSubJobsFromTaskList(job.job_id, ids);
                              //若浮层正展示该名称组合，同步清理其中的记录
                              setComboSubJobs((prev) =>
                                prev.filter((item) => !ids.includes(item.id)),
                              );
                              setClickedSubJobKey(null);
                              message.success("删除成功");
                            } else {
                              message.error("删除失败，请重试");
                            }
                          }}
                          onCancel={(e) => {
                            e.stopPropagation();
                          }}
                          okText="确认"
                          cancelText="取消"
                        >
                          <button
                            className="action-btn"
                            title="删除"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <DeleteOutlined style={{ color: "#FF0000", fontSize: 16 }} />
                          </button>
                        </Popconfirm>
                      </div>
                    }
                  />
                </li>
              );
            })}
        </ul>
      </li>
    );
  }

  /**
   * 分组分支组件
   * 层级结构：job > 分组 > gruops
   */
  // function GroupNode({
  //   job,
  //   isGroupExpanded,
  //   isCategoryExpanded,
  //   selectedNode,
  //   toggleGroup,
  //   toggleCategory,
  //   handleSelect,
  //   onClickEvent,
  //   loadingElement,
  //   setLoadingElement,
  //   setSubJobs,
  //   setPopupInfo,
  //   setCurResultInfo,
  //   setResultDrawOpen,
  // }: {
  //   job: JobData;
  //   isGroupExpanded: (key: string) => boolean;
  //   isCategoryExpanded: (key: string) => boolean;
  //   selectedNode: SelectedNode | null;
  //   toggleGroup: (key: string) => void;
  //   toggleCategory: (key: string) => void;
  //   handleSelect: (node: SelectedNode) => void;
  //   onClickEvent: (taskId: number, type: string, name: string, data: any) => void;
  //   loadingElement: string | null;
  //   setLoadingElement: (key: string | null) => void;
  //   setSubJobs: (data: any[]) => void;
  //   setPopupInfo: (info: any) => void;
  //   setCurResultInfo: (info: any) => void;
  //   setResultDrawOpen: (open: boolean) => void;
  // }) {
  //   const groupKey = `${job.jobname}::分组`;
  //   const expanded = isGroupExpanded(groupKey);
  //   const selected =
  //     selectedNode?.type === "group" &&
  //     selectedNode.job_name === job.jobname &&
  //     selectedNode.group === "分组";

  //   // ----- 内部辅助函数：处理单元操作叶子点击 -----
  //   const elementSelectedHandler = (node: SelectedNode) => {
  //     if (node.data) {
  //       onClickEvent(node.job_id, node.type, node.item_name, node.data);
  //     } else {
  //       message.error(`${node.item_name} 数据有误`);
  //     }
  //   };

  //   // ----- 内部辅助函数：处理流股叶子点击 -----
  //   const streamSelectedHandler = (node: SelectedNode) => {
  //     if (node.data) {
  //       onClickEvent(node.job_id, node.type, node.item_name, node.data);
  //     } else {
  //       message.error(`${node.item_name} 数据有误`);
  //     }
  //   };

  //   // ----- 内部辅助函数：单元操作结果按钮点击 -----
  //   const handleElementResult = async (node: SelectedNode, e: any) => {
  //     if (isLoadingRef.current) return; // 需将 isLoadingRef 提升或使用本地 ref
  //     // ... 同原有 buttonHandler 中 resultElement 逻辑
  //     // 此处为了简洁，建议将 buttonHandler 提升为可传入的 props，或复制逻辑
  //   };

  //   return (
  //     <li className="tree-node level-2">
  //       {/* 二级菜单：分组 */}
  //       <NodeContent
  //         label="分组"
  //         hasChildren={true}
  //         isExpanded={expanded}
  //         isSelected={selected}
  //         onClick={() => {
  //           toggleGroup(groupKey);
  //           handleSelect({
  //             type: "group",
  //             job_id: job.job_id,
  //             job_name: job.jobname,
  //             group: "分组",
  //             path: [job.jobname, "分组"],
  //           });
  //         }}
  //       />
  //       <ul className={`tree-list${expanded ? "" : " collapsed"}`}>
  //         {job.groups.map((group: any) => {
  //           // 每个具体分组的展开 key
  //           const groupItemKey = `${job.jobname}::分组::${group.name}`;
  //           const groupItemExpanded = isGroupExpanded(groupItemKey);
  //           const groupItemSelected =
  //             selectedNode?.type === "groups" &&
  //             selectedNode.job_name === job.jobname &&
  //             selectedNode.item_name === group.name;

  //           return (
  //             <li key={groupItemKey} className="tree-node level-3">
  //               {/* 三级：具体分组（可展开） */}
  //               <NodeContent
  //                 label={group.name}
  //                 hasChildren={true}
  //                 isExpanded={groupItemExpanded}
  //                 isSelected={groupItemSelected}
  //                 onClick={() => {
  //                   toggleGroup(groupItemKey);
  //                   handleSelect({
  //                     type: "groups",
  //                     job_id: job.job_id,
  //                     job_name: job.jobname,
  //                     item_name: group.name,
  //                     path: [job.jobname, "分组", group.name],
  //                     data: group.data,
  //                   });
  //                 }}
  //               />
  //               <ul className={`tree-list${groupItemExpanded ? "" : " collapsed"}`}>
  //                 {/* ----- 分组下的单元操作 ----- */}
  //                 {group.elements && group.elements.length > 0 && (
  //                   <li className="tree-node level-4">
  //                     {/* 二级菜单样式复用，实际是四级 */}
  //                     <NodeContent
  //                       label="单元操作"
  //                       hasChildren={true}
  //                       isExpanded={isGroupExpanded(`${groupItemKey}::单元操作`)}
  //                       isSelected={false}
  //                       onClick={() => {
  //                         toggleGroup(`${groupItemKey}::单元操作`);
  //                       }}
  //                     />
  //                     <ul className={`tree-list${isGroupExpanded(`${groupItemKey}::单元操作`) ? "" : " collapsed"}`}>
  //                       {group.elements.map((el: any) => {
  //                         const catKey = `${groupItemKey}::单元操作::${el.elementname}`;
  //                         const catExpanded = isCategoryExpanded(catKey);
  //                         return (
  //                           <li key={catKey} className="tree-node level-5">
  //                             <NodeContent
  //                               label={el.elementname}
  //                               hasChildren={true}
  //                               isExpanded={catExpanded}
  //                               isSelected={false}
  //                               onClick={() => toggleCategory(catKey)}
  //                             />
  //                             <ul className={`tree-list${catExpanded ? "" : " collapsed"}`}>
  //                               {el.children.map((child: any) => {
  //                                 const childKey = `${catKey}::${child.name}`;
  //                                 const childSelected =
  //                                   selectedNode?.type === "element" &&
  //                                   selectedNode.job_name === job.jobname &&
  //                                   selectedNode.item_name === child.name &&
  //                                   selectedNode.path?.[0] === job.jobname &&
  //                                   selectedNode.path?.[1] === "分组" &&
  //                                   selectedNode.path?.[2] === group.name;

  //                                 // 构建选中节点对象
  //                                 const nodeObj = {
  //                                   type: "element" as const,
  //                                   job_id: job.job_id,
  //                                   job_name: job.jobname,
  //                                   category: el.elementname,
  //                                   item_name: child.name,
  //                                   path: [job.jobname, "分组", group.name, "单元操作", el.elementname, child.name],
  //                                   data: child.data,
  //                                 };

  //                                 return (
  //                                   <li key={childKey} className="tree-node level-6 leaf-node">
  //                                     <NodeContent
  //                                       label={child.name}
  //                                       hasChildren={false}
  //                                       isExpanded={false}
  //                                       isSelected={childSelected}
  //                                       icon={loadingElement === child.name ? <LoadingOutlined /> : undefined}
  //                                       badge={child.data?.isRun ?? false}
  //                                       actions={
  //                                         <ActionButtons
  //                                           actions={ELEMENT_ACTIONS}
  //                                           label={child.name}
  //                                           onBeforeAction={() => handleSelect(nodeObj)}
  //                                           node={nodeObj}
  //                                         />
  //                                       }
  //                                       onClick={() => {
  //                                         handleSelect(nodeObj);
  //                                         elementSelectedHandler(nodeObj);
  //                                       }}
  //                                     />
  //                                   </li>
  //                                 );
  //                               })}
  //                             </ul>
  //                           </li>
  //                         );
  //                       })}
  //                     </ul>
  //                   </li>
  //                 )}

  //                 {/* ----- 分组下的流股 ----- */}
  //                 {group.streams && group.streams.length > 0 && (
  //                   <li className="tree-node level-4">
  //                     <NodeContent
  //                       label="流股"
  //                       hasChildren={true}
  //                       isExpanded={isGroupExpanded(`${groupItemKey}::流股`)}
  //                       isSelected={false}
  //                       onClick={() => {
  //                         toggleGroup(`${groupItemKey}::流股`);
  //                       }}
  //                     />
  //                     <ul className={`tree-list${isGroupExpanded(`${groupItemKey}::流股`) ? "" : " collapsed"}`}>
  //                       {group.streams.map((stream: any) => {
  //                         const streamKey = `${groupItemKey}::流股::${stream.name}`;
  //                         const streamSelected =
  //                           selectedNode?.type === "stream" &&
  //                           selectedNode.job_name === job.jobname &&
  //                           selectedNode.item_name === stream.name &&
  //                           selectedNode.path?.[0] === job.jobname &&
  //                           selectedNode.path?.[1] === "分组" &&
  //                           selectedNode.path?.[2] === group.name;

  //                         const nodeObj = {
  //                           type: "stream" as const,
  //                           job_id: job.job_id,
  //                           job_name: job.jobname,
  //                           item_name: stream.name,
  //                           path: [job.jobname, "分组", group.name, "流股", stream.name],
  //                           data: stream.data,
  //                         };

  //                         return (
  //                           <li key={streamKey} className="tree-node level-5 leaf-node">
  //                             <NodeContent
  //                               label={stream.name}
  //                               hasChildren={false}
  //                               isExpanded={false}
  //                               isSelected={streamSelected}
  //                               actions={
  //                                 <ActionButtons
  //                                   actions={STREAM_ACTIONS}
  //                                   label={stream.name}
  //                                   onBeforeAction={() => handleSelect(nodeObj)}
  //                                   node={nodeObj}
  //                                 />
  //                               }
  //                               onClick={() => {
  //                                 handleSelect(nodeObj);
  //                                 streamSelectedHandler(nodeObj);
  //                               }}
  //                             />
  //                           </li>
  //                         );
  //                       })}
  //                     </ul>
  //                   </li>
  //                 )}
  //               </ul>
  //             </li>
  //           );
  //         })}
  //       </ul>
  //     </li>
  //   );
  // }

  /**
   * 选中节点
   * 更新选中状态，并在控制台输出完整的层级路径信息
   */
  const handleSelect = useCallback((node: SelectedNode,isOpenModal?:boolean) => {
    isInternalRef.current = true;
    setSelectedNode(node);
    if (node.type !== "element" && node.type !== "stream") {
      onClickEvent(null, null, null, null);
    }
    // if(isOpenModal)
    // {
    //   setIsNowNeedOpenPramDrawer(true);
    // }
  }, []);

  const isJobExpanded = (jobname: string) => expandedJobs.has(jobname);
  const isGroupExpanded = (key: string) => expandedGroups.has(key);
  const isCategoryExpanded = (key: string) => expandedCategories.has(key);

  //取消节点选中(不取消节点选中，仅使再次点击选项可以再次打开弹窗)
  const clearSelected = useCallback(() => {
    // isInternalRef.current = true;
    // setSelectedNode(null);

    onClickEvent(null, null, null, null);
  }, [setSelectedNode]);

  const queryJob = async (taskId) => {
    const token = userInfo.access_token;
    const res = await getJob(taskId, token);
    if (res.status === 200) {
      let inputStr = res.data.input;
      if (inputStr) {
        let input = JSON.parse(inputStr);
        return input;
      } else {
        return null;
      }
    } else {
      message.error(`getJob查询失败：${res.status}`);
      return null;
    }
  };

  const querySubJob = async (taskId) => {
    const token = userInfo.access_token;
    const res = await getSubJobs({ job_id: taskId }, token);
    if (res.status === 200) {
      let subJobs = res.data.filter((item) => item.type === "multi");

      return subJobs;
    } else {
      message.error(`getSubJobs查询失败：${res.status}`);
      return [];
    }
  };

  const expandFirstItem = async (selectTaskName, selectTaskId) => {
    let newTaskLists = structuredClone(data);
    let newAlreadyTaskIds = alreadyTaskIds;
    let thisAlreadyTaskIds = [];
    for (let i = 0; i < newTaskLists.length; i++) {
      let taskId = newTaskLists[i].job_id;
      if (!alreadyTaskIds.includes(taskId) && taskId === selectTaskId) {
        let newTaskList = newTaskLists.find((item) => item.job_id === taskId);
        if (newTaskList) {
          let input = await queryJob(taskId);
          let taskListsChildren = await buildNodes(taskId, input);
          let subJobs = await querySubJob(taskId);
          //let groups = await buildGroups(newTreeData??jsonData,taskListsChildren.elements,taskListsChildren.streams);
          newTaskLists[i].elements = taskListsChildren.elements;
          newTaskLists[i].streams = taskListsChildren.streams;
          //newTaskLists[i].groups = groups;
          newTaskLists[i].input = taskListsChildren.input;
          newTaskLists[i].subJobs = subJobs;
          //记录此次查询的task
          thisAlreadyTaskIds.push(taskId);
        }
      }
    }
    if (!isEqual(data, newTaskLists)) {
      //记录已经查询过的task
      thisAlreadyTaskIds.forEach(
        (item) => !newAlreadyTaskIds.includes(item) && newAlreadyTaskIds.push(item),
      );

      setAlreadyTaskIds(newAlreadyTaskIds);
      setTaskList(newTaskLists);
    }
    setLoadingTask(null);
    return true;
  };

  const buildNodes = async (taskId, input) => {
    let nodeDatas = [];
    let linkDatas = [];
    //元件数据
    let jsonNodeDatas = jsonData.nodeDataArray.filter(
      (item) => !item.isGroup && item.type !== "comments" && item.nodeName !== "",
    );
    //流股数据
    let jsonLinkDatas = jsonData.linkDataArray.filter((item) => item.linkName !== "");

    //构建流股与元件的关系
    let inputStreamsMap = {};
    let outputStreamsMap = {};
    for (let i = 0; i < jsonData.linkDataArray.length; i++) {
      let jsonLinkData = jsonData.linkDataArray[i];
      if (jsonLinkData.to) {
        let inputStreamMap = {
          name: jsonLinkData.linkName,
          rule: jsonLinkData.toPort,
          streamAttribute: jsonLinkData.ParamValues?.["流股属性"]?.value ?? "无",
        };
        if (!inputStreamsMap[jsonLinkData.to]) {
          inputStreamsMap[jsonLinkData.to] = [];
        }
        inputStreamsMap[jsonLinkData.to].push(inputStreamMap);
      }
      if (jsonLinkData.from) {
        let outputStreamMap = {
          name: jsonLinkData.linkName,
          rule: jsonLinkData.fromPort,
          streamAttribute: jsonLinkData.ParamValues?.["流股属性"]?.value ?? "无",
        };
        if (!outputStreamsMap[jsonLinkData.from]) {
          outputStreamsMap[jsonLinkData.from] = [];
        }
        outputStreamsMap[jsonLinkData.from].push(outputStreamMap);
      }
    }

    //根据类型分组
    const grouped = jsonNodeDatas.reduce((acc: Record<string, any[]>, item: any) => {
      const type = item.type || "未分类";
      if (!acc[type]) acc[type] = [];
      acc[type].push(item);
      return acc;
    }, {});
    const nodeTypes = Object.keys(grouped);

    for (let i = 0; i < nodeTypes.length; i++) {
      let nodeData = {
        engName: nodeTypes[i],
        elementname: typeMap3[nodeTypes[i]] ?? nodeTypes[i],
        children: [],
      };
      nodeDatas.push(nodeData);
    }

    //填入元件数据
    for (let i = 0; i < nodeDatas.length; i++) {
      let typeNodeData = nodeDatas[i];
      let type = typeNodeData.engName;
      let children = typeNodeData.children;

      let typeJsonNodeDatas = jsonNodeDatas.filter((item) => item.type === type);
      for (let j = 0; j < typeJsonNodeDatas.length; j++) {
        let typeJsonNodeData = typeJsonNodeDatas[j];
        typeJsonNodeData = await buildNodeDataByInput(
          typeJsonNodeData,
          input,
          "node",
          state,
          inputStreamsMap,
          outputStreamsMap,
        );
        let nodeData = {
          name: typeJsonNodeData.nodeName,
          data: typeJsonNodeData,
        };
        children.push(nodeData);
      }

      nodeDatas[i].children = children;
    }

    //填入流股数据
    for (let i = 0; i < jsonLinkDatas.length; i++) {
      let jsonLinkData = jsonLinkDatas[i];
      jsonLinkData = await buildNodeDataByInput(
        jsonLinkData,
        input,
        "link",
        state,
        inputStreamsMap,
        outputStreamsMap,
      );

      let linkData = {
        name: jsonLinkData.linkName,
        data: jsonLinkData,
      };

      linkDatas.push(linkData);
    }

    let datas = {
      elements: nodeDatas,
      streams: linkDatas,
      input: input,
    };
    return datas;
  };

  const buildGroups = async (flowData, elements, streams) => {
    let groupDatas = [];
    if (flowData) {
      let nodes = flowData.nodeDataArray;
      let groups = nodes.filter((item) => item.isGroup);
      if (groups.length > 0) {
        //构建元件分组索引
        let elementGroupMap = {};
        for (let i = 0; i < elements.length; i++) {
          for (let j = 0; j < elements[i].children.length; j++) {
            let element = elements[i].children[j];
            let elementData = element.data;
            let groupKey = elementData.group;
            let elementKey = element.key;

            if (!groupKey) {
              groupKey = "无";
            }

            if (!elementGroupMap[groupKey]) {
              elementGroupMap[groupKey] = [];
            }
            elementGroupMap[groupKey].push(element);
          }
        }

        //构建流股分组索引
        let streamGroupMap = {};
        for (let i = 0; i < streams.length; i++) {
          let stream = streams[i];
          let streamData = stream.data;
          let groupKeys = streamData.group;
          let groupKey1 = groupKeys[0];
          let groupKey2 = groupKeys[1];
          let streamKey = stream.key;

          if (!groupKey1) {
            groupKey1 = "无";
          }
          if (!groupKey2) {
            groupKey2 = "无";
          }

          if (!streamGroupMap[groupKey1]) {
            streamGroupMap[groupKey1] = [];
          }
          streamGroupMap[groupKey1].push(stream);

          if (groupKey1 !== groupKey2) {
            //连接线两端处于不同分组，所以需要录入第二次
            if (!streamGroupMap[groupKey2]) {
              streamGroupMap[groupKey2] = [];
            }
            streamGroupMap[groupKey2].push(stream);
          }
        }

        //无分组
        let group = {
          groupName: "无分组",
          key: "无",
          ParamValues: null,
          color: "transparent",
          isGroup: false,
        };
        groups.push(group);

        //有分组
        for (let i = 0; i < groups.length; i++) {
          let groupData = {
            name: groups[i].groupName,
            key: groups[i].key,
            data: groups[i],
            elements: [],
            streams: [],
          };

          //填充元件数据
          let groupElements = structuredClone(elements);
          let curElementGroup = elementGroupMap[groupData.key];
          let curElementGroupKeys = curElementGroup.map((obj) => obj.data.key);
          for (let j = 0; j < groupElements.length; j++) {
            let groupElementType = groupElements[j];
            groupElementType.children = groupElementType.children.filter((item) =>
              curElementGroupKeys.includes(item.data.key),
            );
            groupElements[j] = groupElementType;
          }
          groupElements = groupElements.filter((item) => item.children.length > 0);
          groupData.elements = groupElements;

          //填充流股数据
          let groupStreams = streamGroupMap[groupData.key];
          groupData.streams = groupStreams;

          groupDatas.push(groupData);
        }
      } else {
        //无分组
        let group = {
          name: "无分组",
          key: "无",
          data: null,
          elements: elements,
          streams: streams,
        };
        groupDatas.push(group);
      }

      return groupDatas;
    } else {
      return groupDatas;
    }
  };

  const showSubJobIcon = (status: string, covered: any) => {
    if (status === "done") {
      if (covered === false) {
        return <span style={{color:statusColor.uncovered,fontSize: 16}}>(未收敛)</span>
        // <WarningFilled style={{ color: `${statusColor.uncovered}`, fontSize: 16 }} />;
      } else {
        return <CheckCircleFilled style={{ color: `${statusColor[status]}`, fontSize: 16 }} />;
      }
    } else if (status === "error") {
      return <WarningFilled style={{ color: `${statusColor[status]}`, fontSize: 16 }} />;
    } else {
      return <LoadingOutlined style={{ color: `${statusColor[status]}`, fontSize: 16 }} />;
    }
  };

  return (
    <div className="tree-container">
      {contextHolder}
      <ul className="tree-list">
        {data.map((job) => {
          const jobExpanded = isJobExpanded(job.jobname);
          const jobSelected =
            selectedNode?.type === "job" && selectedNode.item_name === job.jobname;

          return (
            <li key={job.jobname} className={`tree-node level-1${jobExpanded ? "" : " collapsed"}`}>
              {/* 一级菜单：任务 */}
              <NodeContent
                label={job.jobname}
                hasChildren={true}
                isExpanded={jobExpanded}
                isSelected={jobSelected}
                icon={loadingTask === job.jobname ? <LoadingOutlined /> : undefined}
                actions={
                  <ActionButtons
                    actions={JOB_ACTIONS}
                    label={job.jobname}
                    onBeforeAction={() =>
                      handleSelect({
                        type: "job",
                        job_id: job.job_id,
                        job_name: job.jobname,
                        item_name: job.jobname,
                        path: [job.jobname],
                      })
                    }
                    node={{
                      type: "job",
                      job_id: job.job_id,
                      job_name: job.jobname,
                      item_name: job.jobname,
                      path: [job.jobname],
                    }}
                  />
                }
                onClick={async () => {
                  // 如果当前有任务正在加载，提示并阻止新点击
                  if (loadingTask && loadingTask !== job.jobname) {
                    message.warning("当前任务正在加载，请稍后再试");
                    return;
                  }

                  //判断前一个任务是否已保存
                  if (selectedNode) {
                    if (selectedNode.job_id) {
                      let taskId = selectedNode.job_id;
                      let taskData = data.find((item) => item.job_id === taskId);
                      if (taskData) {
                        if (
                          needSaveTask
                        ) // if((!isEqual(taskData.elements,savedCurTaskJson.elements))||(!isEqual(taskData.streams,savedCurTaskJson.streams)))
                        {
                          //数据不一致，需要执行保存
                          openSaveWarning(taskId);

                          // Modal.confirm({
                          //   content: "该任务未保存，是否确认保存？",
                          //   onOk:() =>
                          //   {
                          //     openSaveWarning(taskId);
                          //   },
                          //   okText: "确认",
                          //   cancelText: "取消",
                          // });
                        }
                      }
                    }
                  }

                  toggleJob(job.jobname, job.job_id);
                  handleSelect({
                    type: "job",
                    job_id: job.job_id,
                    job_name: job.jobname,
                    item_name: job.jobname,
                    path: [job.jobname],
                  });
                }}
              />

              {/* 一级菜单下的子内容（单元操作 + 流股） */}
              <ul className={`tree-list${jobExpanded ? "" : " collapsed"}`}>
                {/* 分组分支 */}
                {/* <GroupNode
                  job={job}
                  isGroupExpanded={isGroupExpanded}
                  isCategoryExpanded={isCategoryExpanded}
                  selectedNode={selectedNode}
                  toggleGroup={toggleGroup}
                  toggleCategory={toggleCategory}
                  handleSelect={handleSelect}
                  onClickEvent={onClickEvent}          // 新增，用于叶子节点点击时触发数据加载
                  loadingElement={loadingElement}      // 新增，用于显示单元操作加载状态
                  setLoadingElement={setLoadingElement} // 新增
                  setSubJobs={setSubJobs}              // 新增，用于弹出单元操作计算结果
                  setPopupInfo={setPopupInfo}          // 新增
                  setCurResultInfo={setCurResultInfo}  // 新增
                  setResultDrawOpen={setResultDrawOpen} // 新增
                /> */}
                {/* <GroupNode
                  job={job}
                  isGroupExpanded={isGroupExpanded}
                  selectedNode={selectedNode}
                  toggleGroup={toggleGroup}
                  handleSelect={handleSelect}
                /> */}

                {/* 单元操作分支 */}
                <UnitOperationNode
                  job={job}
                  isGroupExpanded={isGroupExpanded}
                  isCategoryExpanded={isCategoryExpanded}
                  selectedNode={selectedNode}
                  toggleGroup={toggleGroup}
                  toggleCategory={toggleCategory}
                  handleSelect={handleSelect}
                />

                {/* 流股分支 */}
                <StreamNode
                  job={job}
                  isGroupExpanded={isGroupExpanded}
                  selectedNode={selectedNode}
                  toggleGroup={toggleGroup}
                  handleSelect={handleSelect}
                />

                {/* 组合结果结果分支 */}
                <SubJobsNode
                  job={job}
                  isGroupExpanded={isGroupExpanded}
                  selectedNode={selectedNode}
                  toggleGroup={toggleGroup}
                  handleSelect={handleSelect}
                />
              </ul>
            </li>
          );
        })}
      </ul>
      {popupInfo.visible && (
        <div
          className="click-popup-overlay"
          onClick={() => setPopupInfo({ visible: false, x: 0, y: 0 })}
        >
          <div
            ref={popupRef}
            className="click-popup-content"
            style={popupStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="click-popup-header">
              <span>{selectedNode.item_name}单元计算结果：</span>
              <button
                className="click-popup-close"
                onClick={() => setPopupInfo({ visible: false, x: 0, y: 0 })}
              >
                ×
              </button>
            </div>
            <div className="click-popup-body">
              {subJobs.length ? (
                <ul className="subJobsList">
                  {subJobs.map((i: any, index: number) => {
                    return (
                      <li
                        key={index}
                        onClick={async () => {
                          await setCurResultInfo({
                            id: i.id,
                            type: "subJob",
                          });
                          await setResultDrawOpen(true);

                          //点击后关闭
                          setPopupInfo({ visible: false, x: 0, y: 0 });
                        }}
                      >
                        {/* <span style={{backgroundColor:`${statusColor[i.status]}`}}>{index + 1}</span> */}
                        <b
                          style={{
                            color: `${i.is_converged === false ? statusColor.uncovered : statusColor[i.status]}`,
                          }}
                        >
                          {dayjs(i.created_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss")}
                        </b>
                        <i className={i.status}>{showSubJobIcon(i.status, i.is_converged)}</i>

                        <Popconfirm
                          title="是否确认删除当前计算结果？"
                          zIndex={9000000}
                          onConfirm={async (e) => {
                            e.stopPropagation();
                            let token = userInfo.access_token;
                            let res = await delSubJobs(Number(i.id), token);
                            if (res.status === 200) {
                              setSubJobs(subJobs.filter((item) => item.id !== i.id));
                              message.success("删除成功");
                            }
                          }}
                          onCancel={(e) => {
                            e.stopPropagation();
                          }}
                          okText="确认"
                          cancelText="取消"
                        >
                          <button
                            onClick={(e) => e.stopPropagation()}
                            style={{ marginLeft: 10, backgroundColor: "transparent", border: 0 }}
                            title="删除"
                          >
                            <DeleteOutlined style={{ color: "#FF0000", fontSize: 16 }} />
                          </button>
                        </Popconfirm>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p>此单元没有计算结果。</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 组合计算结果浮层（子任务方式）：列出同名组合各次运行，点击打开该次结果 */}
      {comboPopupInfo.visible && (
        <div
          className="click-popup-overlay"
          onClick={() => setComboPopupInfo({ visible: false, x: 0, y: 0 })}
        >
          <div
            ref={popupRef}
            className="click-popup-content"
            style={popupStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="click-popup-header">
              <span>{`${comboPopupInfo.name || "组合计算"}组合计算结果：`}</span>
              <button
                className="click-popup-close"
                onClick={() => setComboPopupInfo({ visible: false, x: 0, y: 0 })}
              >
                ×
              </button>
            </div>
            <div className="click-popup-body">
              {comboSubJobs.length ? (
                <ul className="subJobsList">
                  {comboSubJobs.map((i: any, index: number) => {
                    return (
                      <li
                        key={index}
                        onClick={async () => {
                          await setCurResultInfo({
                            id: i.id,
                            type: "multiJobs",
                          });
                          await setResultDrawOpen(true);
                          //点击后关闭
                          setComboPopupInfo({ visible: false, x: 0, y: 0 });
                        }}
                      >
                        <b
                          style={{
                            color: `${i.is_converged === false ? statusColor.uncovered : statusColor[i.status]}`,
                          }}
                        >
                          {dayjs(i.created_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss")}
                        </b>
                        <i className={i.status}>{showSubJobIcon(i.status, i.is_converged)}</i>

                        <Popconfirm
                          title="是否确认删除当前计算结果？"
                          zIndex={9000000}
                          onConfirm={async (e) => {
                            e.stopPropagation();
                            let token = userInfo.access_token;
                            let res = await delSubJobs(Number(i.id), token);
                            if (res.status === 200) {
                              setComboSubJobs(comboSubJobs.filter((item) => item.id !== i.id));
                              if (comboPopupInfo.jobId) {
                                removeSubJobsFromTaskList(comboPopupInfo.jobId, [i.id]);
                              }
                              message.success("删除成功");
                            }
                          }}
                          onCancel={(e) => {
                            e.stopPropagation();
                          }}
                          okText="确认"
                          cancelText="取消"
                        >
                          <button
                            onClick={(e) => e.stopPropagation()}
                            style={{ marginLeft: 10, backgroundColor: "transparent", border: 0 }}
                            title="删除"
                          >
                            <DeleteOutlined style={{ color: "#FF0000", fontSize: 16 }} />
                          </button>
                        </Popconfirm>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p>此组合没有计算结果。</p>
              )}
            </div>
          </div>
        </div>
      )}

      {popupInfo2.visible && (
        <div
          className="click-popup-overlay"
          onClick={() => setPopupInfo2({ visible: false, x: 0, y: 0 })}
        >
          <div
            ref={popupRef}
            className="click-popup-content"
            style={popupStyle}
            onClick={(e) => e.stopPropagation()}
          >
            {/* <div className="click-popup-header">
              <span>更多功能：</span>
              <button
                className="click-popup-close"
                onClick={() => setPopupInfo2({ visible: false, x: 0, y: 0 })}
              >
                ×
              </button>
            </div> */}
            <div className="click-popup-body">
              <ul className="subMoreIconList">
                {MORE_ACTIONS.map((item: any, index: number) => {
                  return (
                    <li
                      key={index}
                      onClick={(e) => {
                        // console.log('selesss',selectedNode);
                        buttonHandler(item.key, selectedNode);
                        setPopupInfo2({ visible: false, x: 0, y: 0 });
                      }}
                    >
                      <div>{item.icon}</div>
                      <div className="icontitle">{item.title}</div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* 运行确认弹窗：带最大计算时间输入（分） */}
      <Modal
        title={
          runModalMode === "job"
            ? "是否确认运行任务"
            : runModalMode === "combo"
              ? `是否确认运行组合任务${runModalNode?.name ? `：${runModalNode.name}` : ""}`
              : "是否确认运行单元操作"
        }
        open={runModalOpen}
        onOk={confirmRunWithMaxWait}
        onCancel={() => setRunModalOpen(false)}
        okText="确认"
        cancelText="取消"
        maskClosable={false}
        width={1000}
        zIndex={9000000}
      >
        {/* 输入参数预览：默认收起，展开后可查看本次将要计算的输入参数 */}
        <div style={{ marginBottom: 12 }}>
          <GroupCalParamsPreview
            //每次打开弹窗都回到「收起」的初始状态
            key={runModalOpen ? "run-preview-open" : "run-preview-closed"}
            defaultCollapsed
            jsonData={jsonData}
            selectedElements={
              runModalMode === "element"
                ? runModalNode?.item_name
                  ? [runModalNode.item_name]
                  : []
                : runModalMode === "combo"
                  ? comboSelectedBlocks
                  : (jsonData?.nodeDataArray || [])
                      .filter(
                        (item: any) =>
                          !item.isGroup &&
                          item.elementName &&
                          item.nodeName?.trim() !== "",
                      )
                      .map((item: any) => item.nodeName)
            }
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>最大计算时间：{clickedSubJobKey}</span>
          <InputNumber
            value={maxWaitMinutes}
            onChange={(v) => setMaxWaitMinutes(v ?? null)}
            style={{ width: 120 }}
          />
          <span>分</span>
        </div>
        {/* 仅组合任务：可选择基于该组合过往某次计算成功且收敛的结果进行计算 */}
        {runModalMode === "combo" && (
          <div style={{ marginTop: 12 }}>
            <Checkbox
              checked={useLastSubjob}
              onChange={(e) => {
                const checked = e.target.checked;
                setUseLastSubjob(checked);
                if (!checked) setLastSubjobId(null); //取消勾选时清空已选
              }}
            >
              基于过往组合计算结果进行计算
            </Checkbox>
            {useLastSubjob && (
              <Select
                style={{ width: "100%", marginTop: 8 }}
                placeholder="请选择计算成功且收敛的历史结果"
                value={lastSubjobId ?? undefined}
                onChange={(v) => setLastSubjobId(v)}
                options={(runModalNode?.runs || [])
                  .filter((r: any) => r.status === "done" && r.is_converged !== false)
                  .map((r: any) => ({
                    value: r.id,
                    // label: dayjs(r.created_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss"),
                    //与"查看组合任务"列表项一致：时间后展示完成状态图标
                    label: (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <span>
                          {dayjs(r.created_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss")}
                        </span>
                        <i className={r.status}>{showSubJobIcon(r.status, r.is_converged)}</i>
                      </span>
                    ),
                  }))}
                notFoundContent="暂无计算成功且收敛的结果"
              />
            )}
          </div>
        )}
      </Modal>

      {/* 组合重命名弹窗：整组改名（该组合名下全部运行记录一起改） */}
      <Modal
        title={`重命名组合${renameCombo?.name ? `：${renameCombo.name}` : ""}`}
        open={!!renameCombo}
        onOk={confirmRenameCombo}
        onCancel={() => setRenameCombo(null)}
        okText="确认"
        cancelText="取消"
        confirmLoading={renameComboLoading}
        maskClosable={false}
        zIndex={9000000}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>组合名称：</span>
          <Input
            value={renameComboName}
            onChange={(e) => setRenameComboName(e.target.value)}
            onPressEnter={confirmRenameCombo}
            placeholder="请输入组合名称"
            maxLength={50}
            style={{ flex: 1 }}
          />
        </div>
        {renameCombo && renameCombo.runs.length > 1 && (
          <div style={{ marginTop: 8, color: "#999", fontSize: 12 }}>
            该组合下有 {renameCombo.runs.length} 次运行记录，将一起改名
          </div>
        )}
      </Modal>
    </div>
  );
}
